import React, { useState, useEffect, useLayoutEffect, Fragment } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  InputGroup,
  Button,
  Table,
  Form
} from 'react-bootstrap';
import Flex from 'components/common/Flex';
import HistoryModal from './modal/HistoryModal';
import DatePicker from 'react-datepicker';
import moment from 'moment';

// global
import { useConfirmModalState } from 'global/confirmModalState';
// types
import { ACTION_TYPE, APPROVAL_BUTTON_TEXT } from 'types/type';
import { RESPONSE_CODE } from 'types/const';
// function
import { getLoginInform } from 'function/login';
import { downloadExcelByTable } from 'function/file';

// api
import { factory } from 'api/haccp/commonApi';
import {
  monthEndReporCreate,
  monthEndReporDetail,
  monthEndReporUpdate,
  statisticsCreate,
  monthEndExist,
  approvalList
} from 'api/haccp/report/monthReportApi';

/**
 * 월말보고서 등록 및 상세
 */
const Detail = () => {
  /* router info*/
  const history = useHistory();
  const { state } = useLocation();
  const { seq } = useParams();

  /* recoil */
  const { setConfirmModal } = useConfirmModalState();

  /* state */
  const [modalShow, setModalShow] = useState(false);
  const [actionType, setActionType] = useState('');
  const [validated, setValidated] = useState(false);
  const [factoryOptions, setFactoryOptions] = useState(null);

  const [adminYn, setAdminYn] = useState(null); // 'Y': 관리자, N: 이천,군산 담당자
  const [authority, setAuthority] = useState(null); // DRAFT: 기안자, SECOND: 2차결재자
  const [userSeq, setUserSeq] = useState(null); // 로그인Seq
  const [approvalLeftBtn, setApprovalLeftBtn] = useState(null);
  const [approvalRightBtn, setApprovalRightBtn] = useState(null);
  const [historyList, setHistoryList] = useState([]); // 수정이력

  /**
   * 조치내역 결재 상태값
   * 결재중(3), 수정요청(4), 결재(5)인 경우
   * 입력Form 및 보고서생성버튼 비활성화
   */
  const [isDisabled, setIsDisabled] = useState(false);
  // 월말보고서 보고자료 수정여부
  const [monthEndSttUpdYn, setMonthEndSttUpdYn] = useState('N');
  // report 월말보고자료
  const [isReportExistence, setIsReportExistence] = useState(false);
  // 보고서 설정정보
  const [reportSettingInfo, setReportSettingInfo] = useState({
    pltCd: '', // 공장
    baseYm: new Date(), // 보고서 설정기간
    year: moment(new Date()).format('YYYY'),
    month: moment(new Date()).format('MM'),
    reportNm: '', // 보고서명
    comments: '', // 비고
    approvalStatus: '' // 결재상태
  });

  // 월말보고 자료
  const [reportOriginList, setReportOriginList] = useState({}); // 보고서생성 원본Data
  const [prdtCountList, setPrdtCountList] = useState([]); // 보고서생성-제품 가공Data
  const [procCountList, setProcCountList] = useState([]); // 보고서생성-공정 가공Data
  const [rwmtrCountList, setRwmtrCountList] = useState([]); // 보고서생성-원료 가공Data

  /* event */

  /* 월말보고서 목록페이지 이동 */
  const onList = () => {
    history.push({
      pathname: '/haccp/report/monthend',
      state: state
    });
  };

  /*  보고서 이력목록 API호출 */
  const onHistory = async () => {
    const response = await approvalList(seq);
    const { code, list } = response || {};
    if (code !== RESPONSE_CODE.Success) return;
    setHistoryList(list);
    setModalShow(true);
  };

  /* 달력선택 */
  const onMonthChange = async e => {
    const year = moment(e).format('YYYY');
    const month = moment(e).format('MM');

    const existYn = await reportExistence(year, month);
    if (existYn === 'Y') return;

    if (isReportExistence) {
      setConfirmModal({
        isOpen: true,
        title: '확인',
        message: '보고서 설정기간 변경시 생성된 월말보고자료는 삭제됩니다.',
        isCancelBtn: true,
        isConfirmBtn: true,
        onConfirm: () => {
          setReportOriginList({});
          setPrdtCountList([]);
          setProcCountList([]);
          setRwmtrCountList([]);
          setIsReportExistence(false);
          setReportSettingInfo({
            ...reportSettingInfo,
            baseYm: e,
            year: year,
            month: month
          });
        }
      });
      return;
    }

    setReportSettingInfo({
      ...reportSettingInfo,
      baseYm: e,
      year: year,
      month: month
    });
  };

  /* 보고서 생성 */
  const onReportCreate = async event => {
    const form = event.currentTarget;
    event.preventDefault();
    event.stopPropagation();

    if (form.checkValidity() === false) {
      setValidated(true);
      return;
    }

    const { pltCd, year, month } = reportSettingInfo || {};

    const existYn = await reportExistence(year, month);
    if (existYn === 'Y') return;

    const params = {
      pltCd: pltCd,
      year: Number(year),
      month: Number(month),
      seq: seq
    };

    const response = await statisticsCreate(params);

    const { code, data, message } = response || {};

    if (code !== RESPONSE_CODE.Success) {
      setConfirmModal({
        isOpen: true,
        title: '확인',
        message: message,
        isConfirmBtn: true
      });
      return;
    }

    const {
      prdtCountList = [],
      procCountList = [],
      rwmtrCountList = []
    } = data || {};

    // 월말보고 자료 원본 Data > 서버에 원본Data전달
    setReportOriginList(data);

    // 월말보고자료 제품,공정,원료 Data 가공
    // 제품
    setPrdtCountList(valueTotalOfArray(prdtCountList));
    // 공정
    setProcCountList(valueTotalOfArray(procCountList));
    // 원료
    setRwmtrCountList(valueTotalOfArray(rwmtrCountList));

    // 월말보고서 생성여부
    setIsReportExistence(true);

    // 상세페이지 -> 기존보고서 업데이트한경우
    if (actionType === 'update') {
      setMonthEndSttUpdYn('Y');
    }

    setConfirmModal({
      isOpen: true,
      title: '확인',
      message: '월말보고자료가 생성되었습니다.',
      isConfirmBtn: true
    });
  };

  // 결재 Button Click
  const handleSubmit = buttonSelection => {
    const { comments } = reportSettingInfo || {};
    if (comments === '' && isDisabled === false) {
      setConfirmModal({
        isOpen: true,
        title: '확인',
        message: '비고 입력해주세요.',
        isConfirmBtn: true
      });
      return;
    }

    let approvalText = null;

    switch (buttonSelection) {
      case 'left':
        approvalText = APPROVAL_BUTTON_TEXT[approvalLeftBtn];
        break;
      case 'right':
        approvalText = APPROVAL_BUTTON_TEXT[approvalRightBtn];
        break;
    }

    setConfirmModal({
      isOpen: true,
      title: '확인',
      message: `월말보고서를 ${approvalText} 하시겠습니까?`,
      isCancelBtn: true,
      isConfirmBtn: true,
      onConfirm: () => {
        onSubmit(buttonSelection);
      }
    });
  };

  const onSubmit = async buttonSelection => {
    /*
     * 조치내역 결재 status
     * 1 : 미등록
     * 2 : 기안중
     * 3 : 결재중
     * 4 : 수정요청중
     * 5 : 결재
     *
     * 조치내역 결재상태값에 따른 버튼 status
     * 1: '임시저장'
     * 2: '결재요청'
     * 3: '수정요청'
     * 4: '수정요청 승낙'
     * 5: '수정요청 거절'
     * 6: '결재'
     * 7: '반려'
     */

    // 수정 후 결재 상태(서버요청사항)
    let updApprovalStatus = '';

    switch (buttonSelection) {
      case 'left':
        if (approvalLeftBtn === 1) {
          updApprovalStatus = 2;
        } else if (approvalLeftBtn === 3) {
          updApprovalStatus = 4;
        } else if (approvalLeftBtn === 4) {
          updApprovalStatus = 2;
        } else if (approvalLeftBtn === 6) {
          updApprovalStatus = 5;
        }
        break;
      case 'right':
        if (approvalRightBtn === 2) {
          updApprovalStatus = 3;
        } else if (approvalRightBtn === 5) {
          updApprovalStatus = 3;
        } else if (approvalRightBtn === 7) {
          updApprovalStatus = 2;
        }
        break;
    }

    const { pltCd, year, month, reportNm, comments, approvalStatus } =
      reportSettingInfo || {};

    let requestBody = {};
    let response = {};

    if (actionType === 'create') {
      // 등록
      requestBody = {
        pltCd: pltCd,
        year: Number(year),
        month: Number(month),
        reportNm: reportNm,
        comments: comments,
        regUsrSeq: userSeq,
        approvalStatus: updApprovalStatus,
        reportMonthEndStt: reportOriginList
      };

      response = await monthEndReporCreate(requestBody);
    } else {
      // 수정
      requestBody = {
        seq: Number(seq),
        regUsrSeq: userSeq,
        prevApprovalStatus: approvalStatus,
        updApprovalStatus: updApprovalStatus,
        monthEndSttUpdYn: monthEndSttUpdYn,
        reportNm: reportNm,
        comments: comments,
        reportMonthEndStt: reportOriginList
      };

      response = await monthEndReporUpdate(seq, requestBody);
    }

    const { code, message } = response || {};

    if (code !== RESPONSE_CODE.Success) {
      setConfirmModal({
        isOpen: true,
        title: '확인',
        message: message,
        isConfirmBtn: true
      });
      return;
    }

    let approvalText = null;

    switch (buttonSelection) {
      case 'left':
        approvalText = approvalLeftBtn;
        break;
      case 'right':
        approvalText = approvalRightBtn;
        break;
    }

    setConfirmModal({
      isOpen: true,
      title: '확인',
      message: `월말보고서가 ${APPROVAL_BUTTON_TEXT[approvalText]} 되었습니다.`,
      isConfirmBtn: true
    });

    onList();
  };

  /* 선택월 보고서 존재여부 Check */
  const reportExistence = async (year, month) => {
    if (actionType === 'update') return;

    const { pltCd } = reportSettingInfo || {};

    const params = {
      pltCd: pltCd,
      year: year,
      month: month
    };

    const response = await monthEndExist(params);

    const { code, data, message } = response || {};

    if (code !== RESPONSE_CODE.Success) {
      setConfirmModal({
        isOpen: true,
        title: '확인',
        message: message,
        isConfirmBtn: true
      });
      return;
    }

    const { existYn } = data || {};

    if (existYn === 'Y') {
      setConfirmModal({
        isOpen: true,
        title: '확인',
        message: '선택한 보고서설정기간 월말보고서가 존재합니다.',
        isCancelBtn: false,
        isConfirmBtn: true
      });
    }

    return existYn;
  };

  /* 엑셀다운로드 */
  const onExcelDown = () => {
    const excelTilte = `${reportSettingInfo.month}월 월말 보고 자료.xlsx`;
    const reportTable = document.getElementById('monthEndReport');
    downloadExcelByTable(reportTable, excelTilte);
  };

  /* 상세 API호출 */
  const onDetail = async () => {
    const response = await monthEndReporDetail(seq);
    const { code, data, message } = response || {};

    if (code !== RESPONSE_CODE.Success) {
      setConfirmModal({
        isOpen: true,
        title: '확인',
        message: message,
        isConfirmBtn: true
      });
      return;
    }

    const {
      pltCd,
      year,
      month,
      reportNm,
      comments,
      approvalStatus,
      reportMonthEndStt
    } = data || {};

    setReportSettingInfo({
      ...reportSettingInfo,
      pltCd: pltCd,
      baseYm: new Date(`${year}-${month}`),
      year: year,
      month: month,
      reportNm: reportNm,
      comments: comments,
      approvalStatus: approvalStatus
    });

    /**
     * 조치내역 결재 상태값
     * 결재중(3), 수정요청중(4), 결재(5)인 경우
     * 입력Form 및 보고서생성버튼 비활성화
     */
    if (approvalStatus === 3 || approvalStatus === 4 || approvalStatus === 5) {
      setIsDisabled(true);
    }

    const {
      prdtCountList = [],
      procCountList = [],
      rwmtrCountList = []
    } = reportMonthEndStt || {};

    // 월말보고 자료 원본 Data
    setReportOriginList(reportMonthEndStt);
    // 월말보고자료 제품,공정,원료 Data 가공
    // 제품
    setPrdtCountList(valueTotalOfArray(prdtCountList));
    // 공정
    setProcCountList(valueTotalOfArray(procCountList));
    // 원료
    setRwmtrCountList(valueTotalOfArray(rwmtrCountList));

    // 월말보고서 생성여부
    setIsReportExistence(true);
  };

  // array value Total
  const valueTotalOfArray = array => {
    const totalArray = array?.map(e => ({
      ...e,
      rgstrIngrdAnalTotalSum: sumOfValue(array, 'rgstrIngrdAnalTotal'),
      rgstrIngrdExcdCntSum: sumOfValue(array, 'rgstrIngrdExcdCnt'),
      aftAnalTotalSum: sumOfValue(array, 'aftAnalTotal'),
      aftExcdCntSum: sumOfValue(array, 'aftExcdCnt'),
      otaAnalTotalSum: sumOfValue(array, 'otaAnalTotal'),
      otaExcdCntSum: sumOfValue(array, 'otaExcdCnt'),
      salmoAnalTotalSum: sumOfValue(array, 'salmoAnalTotal'),
      salmoExcdCntSum: sumOfValue(array, 'salmoExcdCnt'),
      mthvessAnalTotalSum: sumOfValue(array, 'mthvessAnalTotal'),
      mthvessExcdCntSum: sumOfValue(array, 'mthvessExcdCnt')
    }));

    return totalArray;
  };

  // value Sum
  const sumOfValue = (array, key) => {
    let valueSum = array.reduce(
      (prev, current) => prev + Number(current[key]),
      0
    );

    if (Number(valueSum) === 0) {
      valueSum = '-';
    }

    return valueSum;
  };

  /* 공장목록 API */
  const factoryList = async () => {
    const response = await factory();

    const { code, list } = response || {};
    if (code !== RESPONSE_CODE.Success) return;

    setFactoryOptions(list);
  };

  /* 로그인 정보 */
  const loginInfo = () => {
    const { adminYn, pltCd, seq, haccpAuth } = getLoginInform();

    if (adminYn === 'Y') {
      setAdminYn(adminYn);
      return;
    }

    // 1차,2차결재자인 경우 입력Form 및 보고서생성버튼 비활성화
    if (haccpAuth === 'FIRST' || haccpAuth === 'SECOND') {
      setIsDisabled(true);
    }

    setReportSettingInfo(prevState => ({
      ...prevState,
      pltCd: pltCd
    }));

    // 로그인 user Seq
    setUserSeq(seq);
    // 관리자여부
    setAdminYn(adminYn);
    // 결재권한
    setAuthority(haccpAuth);
  };

  /* watch */
  useEffect(() => {
    // 공장 API 호출
    factoryList();
    // 로그인 정보
    loginInfo();
  }, []);

  useEffect(() => {
    /**
     * 조치내역 결재 상태값(approvalStatus)
     * 1 : 미등록
     * 2 : 기안중
     * 3 : 결재중
     * 4 : 수정요청중
     * 5 : 결재
     *
     * 조치내역 상태값에 따른 버튼 TEXT
     * 1: '임시저장'
     * 2: '결재요청'
     * 3: '수정요청'
     * 4: '수정요청 승낙'
     * 5: '수정요청 거절'
     * 6: '결재'
     * 7: '반려'
     */

    const { approvalStatus } = reportSettingInfo || {};

    setApprovalLeftBtn(null);
    setApprovalRightBtn(null);

    // 결재권한 및 결재상태값에 따른 버튼설정
    switch (authority) {
      case 'DRAFT':
        // 기안자
        if (approvalStatus === 1 || approvalStatus === 2) {
          setApprovalLeftBtn(1);
          setApprovalRightBtn(2);
        } else if (approvalStatus === 3) {
          setApprovalLeftBtn(3);
        }
        break;
      case 'SECOND':
        // 2차결재자
        if (approvalStatus === 3) {
          setApprovalLeftBtn(6);
          setApprovalRightBtn(7);
        } else if (approvalStatus === 4) {
          setApprovalLeftBtn(4);
          setApprovalRightBtn(5);
        }
        break;
    }
  }, [reportSettingInfo.approvalStatus, authority]);

  useEffect(() => {
    switch (actionType) {
      case ACTION_TYPE.CREATE: {
        // 기안자인 경우
        if (authority === 'DRAFT') {
          setApprovalLeftBtn(1);
          setApprovalRightBtn(2);
        }
        break;
      }
      case ACTION_TYPE.UPDATE: {
        // 상세 API호출
        onDetail();
        break;
      }
    }
  }, [actionType]);

  useLayoutEffect(() => {
    if (seq) {
      setActionType('update');
      return;
    }

    setActionType('create');
  }, []);

  return (
    <>
      <Form noValidate validated={validated} onSubmit={onReportCreate}>
        <Card className="mb-3">
          <Card.Header>
            <Row className="align-items-center g-2">
              <Col>
                <h5 className="mb-0">보고서 설정 정보</h5>
              </Col>
            </Row>
          </Card.Header>
          <Card.Body className="bg-light">
            <Row className="g-3">
              <Col sm={4}>
                <Form.Group>
                  <Form.Label>*공장</Form.Label>
                  <InputGroup>
                    <Form.Select
                      required
                      size="sm"
                      onChange={({ target: { value } }) =>
                        setReportSettingInfo({
                          ...reportSettingInfo,
                          pltCd: value
                        })
                      }
                      value={reportSettingInfo.pltCd}
                      disabled={
                        adminYn === 'N' ||
                        reportSettingInfo.approvalStatus !== ''
                      }
                    >
                      <option hidden value="">
                        공장선택
                      </option>
                      {factoryOptions?.map(option => (
                        <option key={option.pltCd} value={option.pltCd}>
                          {option.pltNmKo}
                        </option>
                      ))}
                    </Form.Select>
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col sm={4}>
                <Form.Group>
                  <Form.Label>*보고서 설정 기간</Form.Label>
                  <InputGroup>
                    <DatePicker
                      onKeyDown={e => {
                        e.preventDefault();
                      }}
                      locale="ko"
                      dateFormat="yyyy년 MM월"
                      className="form-control form-control-sm"
                      showMonthYearPicker
                      onChange={e => onMonthChange(e)}
                      selected={reportSettingInfo.baseYm}
                      maxDate={new Date()}
                      disabled={
                        adminYn === 'Y' ||
                        reportSettingInfo.approvalStatus !== ''
                      }
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col sm={4}>
                <Form.Group>
                  <Form.Label>*보고서명</Form.Label>
                  <InputGroup>
                    <Form.Control
                      required
                      placeholder="보고서명을 입력해주세요."
                      type="text"
                      size="sm"
                      maxLength="20"
                      value={reportSettingInfo.reportNm || ''}
                      onChange={({ target: { value } }) =>
                        setReportSettingInfo({
                          ...reportSettingInfo,
                          reportNm: value
                        })
                      }
                      disabled={adminYn === 'Y' || isDisabled}
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
          <Card.Footer className="d-flex align-items-center">
            {adminYn === 'N' && isDisabled === false && (
              <Button size="sm" className="ms-auto" type="submit">
                보고서 생성
              </Button>
            )}
            {isReportExistence === false && (
              <Button
                size="sm"
                variant="secondary"
                className="ms-2"
                onClick={() => onList()}
              >
                목록
              </Button>
            )}
          </Card.Footer>
        </Card>
        {/* 보고서 생성*/}
        {isReportExistence && (
          <Card>
            <Card.Header>
              <Row className="align-items-center g-2">
                <Col>
                  <h5 className="mb-0">
                    [{reportSettingInfo.month || '-'}월] 월말 보고 자료
                  </h5>
                </Col>
                <Col xs={'auto'}>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={onExcelDown}
                    disabled={reportOriginList?.length === 0}
                  >
                    엑셀 다운로드
                  </Button>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body>
              <Table bordered className="fs--1 text-center" id="monthEndReport">
                <thead className="bg-200 text-700 align-middle border-300">
                  <tr>
                    <th className="text-nowrap" colSpan={2} rowSpan={2}>
                      분할 내역
                    </th>
                    <th className="text-nowrap" colSpan={2}>
                      일반성분
                    </th>
                    <th className="text-nowrap" colSpan={2}>
                      아플라톡신
                    </th>
                    <th className="text-nowrap" colSpan={2}>
                      오클라톡신
                    </th>
                    <th className="text-nowrap" colSpan={2}>
                      살모넬라
                    </th>
                    <th className="text-nowrap" colSpan={2}>
                      차기모선
                    </th>
                  </tr>
                  <tr>
                    <th className="text-nowrap">분석건수</th>
                    <th className="text-nowrap">이탈건수</th>
                    <th className="text-nowrap">분석건수</th>
                    <th className="text-nowrap">이탈건수</th>
                    <th className="text-nowrap">분석건수</th>
                    <th className="text-nowrap">이탈건수</th>
                    <th className="text-nowrap">분석건수</th>
                    <th className="text-nowrap">이탈건수</th>
                    <th className="text-nowrap">분석건수</th>
                    <th className="text-nowrap">이탈건수</th>
                  </tr>
                </thead>
                {/* 제품 */}
                <tbody>
                  {prdtCountList?.map(
                    (
                      {
                        ctgryGrpNm,
                        itemNm,
                        rgstrIngrdAnalTotal,
                        rgstrIngrdExcdCnt,
                        aftAnalTotal,
                        aftExcdCnt,
                        otaAnalTotal,
                        otaExcdCnt,
                        salmoAnalTotal,
                        salmoExcdCnt,
                        mthvessAnalTotal,
                        mthvessExcdCnt
                      },
                      index
                    ) => (
                      <tr key={index}>
                        {index === 0 && (
                          <th
                            className="bg-200 text-700 border-300 border-y align-middle"
                            rowSpan={prdtCountList.length}
                          >
                            {ctgryGrpNm || '-'}
                          </th>
                        )}
                        <th className="bg-200 text-700 border-300 border-y">
                          {itemNm || '-'}
                        </th>
                        <td>{rgstrIngrdAnalTotal || '-'}</td>
                        <td>{rgstrIngrdExcdCnt || '-'}</td>
                        <td>{aftAnalTotal || '-'}</td>
                        <td>{aftExcdCnt || '-'}</td>
                        <td>{otaAnalTotal || '-'}</td>
                        <td>{otaExcdCnt || '-'}</td>
                        <td>{salmoAnalTotal || '-'}</td>
                        <td>{salmoExcdCnt || '-'}</td>
                        <td>{mthvessAnalTotal || '-'}</td>
                        <td>{mthvessExcdCnt || '-'}</td>
                      </tr>
                    )
                  )}
                  {/* 제품 합계 */}
                  {prdtCountList?.map(
                    (
                      {
                        rgstrIngrdAnalTotalSum,
                        rgstrIngrdExcdCntSum,
                        aftAnalTotalSum,
                        aftExcdCntSum,
                        otaAnalTotalSum,
                        otaExcdCntSum,
                        salmoAnalTotalSum,
                        salmoExcdCntSum,
                        mthvessAnalTotalSum,
                        mthvessExcdCntSum
                      },
                      index
                    ) => (
                      <Fragment key={index}>
                        {index === 0 && (
                          <tr className="bg-200 border-300 border-y">
                            <td colSpan={2}>합계</td>
                            <td>{rgstrIngrdAnalTotalSum || '-'}</td>
                            <td>{rgstrIngrdExcdCntSum || '-'}</td>
                            <td>{aftAnalTotalSum || '-'}</td>
                            <td>{aftExcdCntSum || '-'}</td>
                            <td>{otaAnalTotalSum || '-'}</td>
                            <td>{otaExcdCntSum || '-'}</td>
                            <td>{salmoAnalTotalSum || '-'}</td>
                            <td>{salmoExcdCntSum || '-'}</td>
                            <td>{mthvessAnalTotalSum || '-'}</td>
                            <td>{mthvessExcdCntSum || '-'}</td>
                          </tr>
                        )}
                      </Fragment>
                    )
                  )}
                </tbody>
                {/* 공정 */}
                <tbody>
                  {procCountList?.map(
                    (
                      {
                        ctgryGrpNm,
                        itemNm,
                        rgstrIngrdAnalTotal,
                        rgstrIngrdExcdCnt,
                        aftAnalTotal,
                        aftExcdCnt,
                        otaAnalTotal,
                        otaExcdCnt,
                        salmoAnalTotal,
                        salmoExcdCnt,
                        mthvessAnalTotal,
                        mthvessExcdCnt
                      },
                      index
                    ) => (
                      <tr key={index}>
                        {index === 0 && (
                          <th
                            className="bg-200 text-700 border-300 border-y align-middle"
                            rowSpan={procCountList.length}
                          >
                            {ctgryGrpNm || '-'}
                          </th>
                        )}
                        <th className="bg-200 text-700 border-300 border-y">
                          {itemNm || '-'}
                        </th>
                        <td>{rgstrIngrdAnalTotal || '-'}</td>
                        <td>{rgstrIngrdExcdCnt || '-'}</td>
                        <td>{aftAnalTotal || '-'}</td>
                        <td>{aftExcdCnt || '-'}</td>
                        <td>{otaAnalTotal || '-'}</td>
                        <td>{otaExcdCnt || '-'}</td>
                        <td>{salmoAnalTotal || '-'}</td>
                        <td>{salmoExcdCnt || '-'}</td>
                        <td>{mthvessAnalTotal || '-'}</td>
                        <td>{mthvessExcdCnt || '-'}</td>
                      </tr>
                    )
                  )}
                  {/* 공정 합계*/}
                  {procCountList?.map(
                    (
                      {
                        rgstrIngrdAnalTotalSum,
                        rgstrIngrdExcdCntSum,
                        aftAnalTotalSum,
                        aftExcdCntSum,
                        otaAnalTotalSum,
                        otaExcdCntSum,
                        salmoAnalTotalSum,
                        salmoExcdCntSum,
                        mthvessAnalTotalSum,
                        mthvessExcdCntSum
                      },
                      index
                    ) => (
                      <Fragment key={index}>
                        {index === 0 && (
                          <tr className="bg-200 border-300 border-y">
                            <td colSpan={2}>합계</td>
                            <td>{rgstrIngrdAnalTotalSum || '-'}</td>
                            <td>{rgstrIngrdExcdCntSum || '-'}</td>
                            <td>{aftAnalTotalSum || '-'}</td>
                            <td>{aftExcdCntSum || '-'}</td>
                            <td>{otaAnalTotalSum || '-'}</td>
                            <td>{otaExcdCntSum || '-'}</td>
                            <td>{salmoAnalTotalSum || '-'}</td>
                            <td>{salmoExcdCntSum || '-'}</td>
                            <td>{mthvessAnalTotalSum || '-'}</td>
                            <td>{mthvessExcdCntSum || '-'}</td>
                          </tr>
                        )}
                      </Fragment>
                    )
                  )}
                </tbody>
                {/* 원료 */}
                <tbody>
                  {rwmtrCountList?.map(
                    (
                      {
                        ctgryGrpNm,
                        itemNm,
                        rgstrIngrdAnalTotal,
                        rgstrIngrdExcdCnt,
                        aftAnalTotal,
                        aftExcdCnt,
                        otaAnalTotal,
                        otaExcdCnt,
                        salmoAnalTotal,
                        salmoExcdCnt,
                        mthvessAnalTotal,
                        mthvessExcdCnt
                      },
                      index
                    ) => (
                      <tr key={index}>
                        {index === 0 && (
                          <th
                            className="bg-200 text-700 border-300 border-y align-middle"
                            rowSpan={rwmtrCountList.length}
                          >
                            {ctgryGrpNm || '-'}
                          </th>
                        )}
                        <th className="bg-200 text-700 border-300 border-y">
                          {itemNm || '-'}
                        </th>
                        <td>{rgstrIngrdAnalTotal || '-'}</td>
                        <td>{rgstrIngrdExcdCnt || '-'}</td>
                        <td>{aftAnalTotal || '-'}</td>
                        <td>{aftExcdCnt || '-'}</td>
                        <td>{otaAnalTotal || '-'}</td>
                        <td>{otaExcdCnt || '-'}</td>
                        <td>{salmoAnalTotal || '-'}</td>
                        <td>{salmoExcdCnt || '-'}</td>
                        <td>{mthvessAnalTotal || '-'}</td>
                        <td>{mthvessExcdCnt || '-'}</td>
                      </tr>
                    )
                  )}
                  {/* 원료 합계*/}
                  {rwmtrCountList?.map(
                    (
                      {
                        rgstrIngrdAnalTotalSum,
                        rgstrIngrdExcdCntSum,
                        aftAnalTotalSum,
                        aftExcdCntSum,
                        otaAnalTotalSum,
                        otaExcdCntSum,
                        salmoAnalTotalSum,
                        salmoExcdCntSum,
                        mthvessAnalTotalSum,
                        mthvessExcdCntSum
                      },
                      index
                    ) => (
                      <Fragment key={index}>
                        {index === 0 && (
                          <tr className="bg-200 border-300 border-y">
                            <td colSpan={2}>합계</td>
                            <td>{rgstrIngrdAnalTotalSum || '-'}</td>
                            <td>{rgstrIngrdExcdCntSum || '-'}</td>
                            <td>{aftAnalTotalSum || '-'}</td>
                            <td>{aftExcdCntSum || '-'}</td>
                            <td>{otaAnalTotalSum || '-'}</td>
                            <td>{otaExcdCntSum || '-'}</td>
                            <td>{salmoAnalTotalSum || '-'}</td>
                            <td>{salmoExcdCntSum || '-'}</td>
                            <td>{mthvessAnalTotalSum || '-'}</td>
                            <td>{mthvessExcdCntSum || '-'}</td>
                          </tr>
                        )}
                      </Fragment>
                    )
                  )}
                </tbody>
              </Table>
              <Form.Group>
                <Flex alignItems={'center'} className="mb-3">
                  비고
                  {actionType === 'update' && (
                    <Button
                      size="sm"
                      className="ms-auto"
                      onClick={() => onHistory()}
                    >
                      이력
                    </Button>
                  )}
                </Flex>
                <Form.Control
                  as="textarea"
                  rows="3"
                  className="form-controll h-75"
                  placeholder="100자 이내로 입력해주세요."
                  value={reportSettingInfo.comments || ''}
                  onChange={e =>
                    setReportSettingInfo({
                      ...reportSettingInfo,
                      comments: e.target.value
                    })
                  }
                  disabled={adminYn === 'Y' || isDisabled}
                />
              </Form.Group>
            </Card.Body>
            <Card.Footer className="d-flex align-items-center">
              <Button
                size="sm"
                variant="secondary"
                className="ms-auto"
                onClick={() => onList()}
              >
                목록
              </Button>
              {approvalLeftBtn !== null && (
                <Button
                  size="sm"
                  className="ms-2"
                  onClick={() => handleSubmit('left')}
                >
                  {APPROVAL_BUTTON_TEXT[approvalLeftBtn]}
                </Button>
              )}
              {approvalRightBtn !== null && (
                <Button
                  size="sm"
                  className="ms-2"
                  onClick={() => handleSubmit('right')}
                >
                  {APPROVAL_BUTTON_TEXT[approvalRightBtn]}
                </Button>
              )}
            </Card.Footer>
          </Card>
        )}
      </Form>
      {/*수정이력 Modal*/}
      {modalShow === true && (
        <HistoryModal
          modalShow={modalShow}
          setModalShow={setModalShow}
          historyList={historyList}
        ></HistoryModal>
      )}
    </>
  );
};

export default Detail;
